import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

export default function Pricing() {
  const { t } = useI18n();

  const plans = [
    {
      title: t("plan_free"),
      desc: t("plan_free_desc"),
      price: "$0",
      per: "",
      features: t("plan_free_features"),
      cta: t("plan_cta_free"),
      ctaTo: "/register",
      ctaTestId: "pricing-free-cta",
      highlight: false,
    },
    {
      title: t("plan_pro"),
      desc: t("plan_pro_desc"),
      price: t("plan_pro_price"),
      per: t("plan_pro_per"),
      features: t("plan_pro_features"),
      cta: t("plan_cta_pro"),
      ctaTo: "/register",
      ctaTestId: "pricing-pro-cta",
      highlight: true,
    },
  ];

  return (
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <div className="aurora opacity-50" />

      <div className="relative text-center mb-14 z-10">
        <span className="chip"><Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Pricing</span>
        <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight mt-5" data-testid="pricing-title">
          {t("pricing_title")}
        </h1>
        <p className="mt-4 text-lg text-zinc-600 max-w-md mx-auto">{t("pricing_sub")}</p>
      </div>

      <div className="relative grid md:grid-cols-2 gap-5 z-10">
        {plans.map((plan, i) => (
          <div
            key={i}
            data-testid={`pricing-card-${i}`}
            className={`surface-card p-8 sm:p-10 flex flex-col gap-5 relative ${plan.highlight ? "ring-1 ring-zinc-900/10" : ""}`}
            style={
              plan.highlight
                ? { background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAF7 100%)" }
                : undefined
            }
          >
            {plan.highlight && (
              <span className="absolute -top-3 inset-x-0 mx-auto w-fit chip bg-zinc-900 text-white border-zinc-900">
                {t("early_access_chip")}
              </span>
            )}

            <div>
              <h3 className="font-display text-xl font-semibold tracking-tight">{plan.title}</h3>
              <p className="text-sm text-zinc-500 mt-1.5">{plan.desc}</p>
            </div>

            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-5xl font-bold tracking-tight">{plan.price}</span>
              <span className="text-zinc-500 font-medium">{plan.per}</span>
              {plan.highlight && (
                <span className="text-zinc-400 text-sm line-through ms-2" dir="ltr">
                  {t("plan_pro_original")}
                </span>
              )}
            </div>

            {plan.highlight && (
              <p className="text-xs text-zinc-500 -mt-2 leading-relaxed">
                {t("early_access_banner")}
              </p>
            )}

            <ul className="space-y-2.5">
              {(Array.isArray(plan.features) ? plan.features : []).map((f, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-zinc-900 text-white grid place-items-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="text-zinc-700">{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to={plan.ctaTo}
              data-testid={plan.ctaTestId}
              className={`btn btn-lg mt-auto ${plan.highlight ? "btn-primary" : "btn-secondary"}`}
            >
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
